// hooks/use-dialog-routing.ts
import { useEffect, useRef } from "react";
import { create } from "zustand";

// 🚀 1. The Global Navigation Flag to prevent dominos
export const dialogNavigationState = {
  isProgrammaticBack: false,
};

interface DialogState {
  id: string;
  onClose: () => void;
}

interface DialogSystemStore {
  stack: DialogState[];
  push: (dialog: DialogState) => void;
  remove: (id: string) => void;
  popTop: () => void;
}

export const useDialogSystemStore = create<DialogSystemStore>((set, get) => ({
  stack: [],
  push: (dialog) =>
    set((state) => {
      const filtered = state.stack.filter((d) => d.id !== dialog.id);
      return { stack: [...filtered, dialog] };
    }),
  remove: (id) =>
    set((state) => ({
      stack: state.stack.filter((d) => d.id !== id),
    })),
  popTop: () => {
    const { stack } = get();
    if (stack.length === 0) return;
    const top = stack[stack.length - 1];
    top.onClose();
  },
}));

export function useDialogRouting(
  id: string,
  isOpen: boolean,
  onClose: () => void,
) {
  const push = useDialogSystemStore((s) => s.push);
  const remove = useDialogSystemStore((s) => s.remove);

  const onCloseRef = useRef(onClose);
  useEffect(() => {
    onCloseRef.current = onClose;
  }, [onClose]);

  useEffect(() => {
    // --- IF DIALOG IS CLOSED VIA UI BUTTON ---
    if (!isOpen) {
      remove(id);
      if (
        typeof window !== "undefined" &&
        window.history.state?.dialogId === id
      ) {
        // 🚀 2. Tell the manager to IGNORE the next popstate
        dialogNavigationState.isProgrammaticBack = true;
        window.history.back();
      }
      return;
    }

    // --- IF DIALOG IS OPENED ---
    push({ id, onClose: () => onCloseRef.current() });

    if (
      typeof window !== "undefined" &&
      window.history.state?.dialogId !== id
    ) {
      window.history.pushState({ ...window.history.state, dialogId: id }, "");
    }

    return () => remove(id);
  }, [isOpen, id, push, remove]);
}
